import axios, { AxiosError, AxiosHeaders, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import i18next from 'i18next';

import { memoizedRefreshedSession } from './refreshTokenQueries';
import {
  getCookie,
  isPotentiallySlowRequest,
  retrieveSessionFromStorage
} from '_helpers/queryInterceptorHelpers';
import { constants } from 'appConstants';

import type { Session } from '@/types/auth';
import type { RetryableAxiosRequestConfig } from '@/types/api';

const testerlyHostNames = ["localhost", "127.0.0.1", "survey.testerly.com"];
const isTesterlyHostName = testerlyHostNames.includes(window.location.hostname);
const isUsingJwt = !!sessionStorage.getItem('session')
const isCookieCredentialsAllowed = !isUsingJwt && isTesterlyHostName;
const apiVersion: string = import.meta.env.VITE_API_VERSION ?? '1.3.0';

const axiosApiInstance: AxiosInstance = axios.create({
  baseURL: constants.apiUrl,
  headers: {
    Accept: `application/vnd.testerly+json; version=${apiVersion}`
  },
  responseType: 'json',
  responseEncoding: 'utf8',
  timeout: 20000,
  withCredentials: isCookieCredentialsAllowed
});

/**
 * Request interceptor.
 *
 * - Appends CSRF, authorization, and language headers.
 * - Extends timeout to 60 seconds for potentially longer requests (surveys, progressions, logins); 20s otherwise.
 */
axiosApiInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    const session: Session | null = retrieveSessionFromStorage();
    // noinspection SpellCheckingInspection
    const csrfToken: string = getCookie("csrftoken");
    // noinspection SpellCheckingInspection
    const cookieSessionId: string = getCookie("sessionid");

    // Ensure config.headers is an instance of AxiosHeaders
    if (!config.headers) {
      config.headers = new AxiosHeaders(config.headers);
    }

    // Extend timeout to 60 seconds for potentially longer requests (surveys, progressions, logins); 20s otherwise.
    config.timeout = isPotentiallySlowRequest(config.url || '') ? 60000 : 20000;

    // Append CSRF token if available
    if (csrfToken) {
      config.headers.set("X-CSRFToken", csrfToken);
    }

    // Append access token if available and no session cookie is present
    if (session?.accessToken && !cookieSessionId) {
      config.headers.set("Authorization", `Bearer ${session.accessToken}`);
    }

    // Append language preference
    config.headers.set("Accept-Language", i18next.language);

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

/**
 * When a response fails with a 401 (unauthorized) error, refresh the session and retry the request.
 */
axiosApiInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response, async (error: AxiosError): Promise<AxiosResponse> => {
    const config = error.config as RetryableAxiosRequestConfig;

    /**
     * Reject if:
     * - Not a 401 error
     * - The request has already been retried
     * - No Authorization header was originally sent
     */
    if (
      error?.response?.status !== 401 ||
      config?.sent === true ||
      !config?.headers?.Authorization
    ) {
      return Promise.reject(error);
    }

    /**
     * Retry on 401 with a refresh token
     */

    // Mark the request as retried
    config.sent = true;

    // Attempt to refresh the session
    const refreshedSession = await memoizedRefreshedSession();
    if (!refreshedSession?.accessToken) {
      return Promise.reject(error);
    }

    // Ensure the "headers" object exists
    if (!config.headers) {
      config.headers = new AxiosHeaders(config.headers);
    }

    // Update the Authorization header with the new access token
    config.headers.set('Authorization', `Bearer ${refreshedSession.accessToken}`);

    // Retry the request
    return axios(config);
  }
);

export const api: AxiosInstance = axiosApiInstance;